import React, { Fragment, createRef, useContext } from 'react';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import BootstrapTable from 'react-bootstrap-table-next';
import Badge from 'reactstrap/es/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Row } from 'reactstrap';
import ButtonIcon from '../../common/ButtonIcon';
import { Link } from 'react-router-dom';
import AppContext from '../../../context/Context';
import { withTranslation } from 'react-i18next';

const CustomTotal = ({ sizePerPage, totalSize, page, lastIndex }) => (
  <span>
    {(page - 1) * sizePerPage + 1} to {lastIndex > totalSize ? totalSize : lastIndex} of {totalSize} —{' '}
  </span>
);

const nameFormatter = nameuuid => {
  if (!nameuuid || typeof nameuuid !== 'string') {
    return <span>-</span>;
  }
  const uuid = nameuuid.length >= 36 ? nameuuid.substring(nameuuid.length - 36, nameuuid.length) : '';
  const name = nameuuid.length >= 36 ? nameuuid.substring(0, nameuuid.length - 36) : nameuuid;
  return (
    <Link
      to={'/singlemicrogrid/details?uuid=' + uuid}
      target="_blank"
      className="font-weight-semi-bold"
    >
      {name}
    </Link>
  );
};

const SelectRowInput = ({ indeterminate, rowIndex, ...rest }) => (
  <div className="custom-control custom-checkbox">
    <input
      className="custom-control-input"
      {...rest}
      onChange={() => {}}
      ref={input => {
        if (input) input.indeterminate = indeterminate;
      }}
    />
    <label className="custom-control-label" />
  </div>
);

const selectRow = onSelect => ({
  mode: 'checkbox',
  clickToSelect: false,
  selectionHeaderRenderer: ({ mode, ...rest }) => <SelectRowInput type="checkbox" {...rest} />,
  selectionRenderer: ({ mode, ...rest }) => <SelectRowInput type={mode} {...rest} />,
  headerColumnStyle: { border: 0, verticalAlign: 'middle' },
  selectColumnStyle: { border: 0, verticalAlign: 'middle' },
  onSelect: onSelect,
  onSelectAll: onSelect
});

const MicrogridTable = ({ setIsSelected, microgridList, t }) => {
  const energyFormatter = amount => <Fragment>{amount} kWh</Fragment>;
  const capacityFormatter = amount => <Fragment>{amount} kWh</Fragment>;
  const powerFormatter = amount => <Fragment>{amount} kW</Fragment>;

  const { currency } = useContext(AppContext);
  const currencyFormatter = amount => (
    <Fragment>
      {amount} {currency}
    </Fragment>
  );

  let table = createRef();
  const handleNextPage = ({ page, onPageChange }) => () => {
    onPageChange(page + 1);
  };

  const handlePrevPage = ({ page, onPageChange }) => () => {
    onPageChange(page - 1);
  };

  const handleViewAll = ({ onSizePerPageChange }, newSizePerPage) => {
    onSizePerPageChange(newSizePerPage, 1);
  };

  const onSelect = () => {
    setImmediate(() => {
      setIsSelected(!!table.current.selectionContext.selected.length);
    });
  };

  const options = {
    custom: true,
    sizePerPage: 6,
    totalSize: microgridList.length
  };

  const statusFormatter = status => {
    let color = '';
    let icon = '';
    let text = '';
    switch (status) {
      case 'online':
        color = 'success';
        icon = 'check';
        text = t('Communication Online');
        break;
      case 'charging':
        color = 'success';
        icon = 'check';
        text = 'Charging';
        break;
      case 'discharging':
        color = 'success';
        icon = 'check';
        text = 'Discharging';
        break;
      case 'offline':
        color = 'secondary';
        icon = 'ban';
        text = t('Communication Offline');
        break;
      default:
        color = 'warning';
        icon = 'stream';
        text = 'Idling';
    }
    return (
      <Badge color={`soft-${color}`} className="rounded-capsule">
        {text}
        <FontAwesomeIcon icon={icon} transform="shrink-2" className="ml-1" />
      </Badge>
    );
  };
  const columns = [
    {
      dataField: 'nameuuid',
      text: t('Name'),
      formatter: nameFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'address',
      text: t('Address'),
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'postal_code',
      text: t('Postal Code'),
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'subtotal_charge_energy',
      text: t('Total Charge'),
      formatter: energyFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'subtotal_discharge_energy',
      text: t('Total Discharge'),
      formatter: energyFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'subtotal_discharge_billing',
      text: t('Total Revenue'),
      formatter: currencyFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'rated_capacity',
      text: t('Rated Capacity'),
      formatter: capacityFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'rated_power',
      text: t('Rated Power'),
      formatter: powerFormatter,
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      sort: true
    },
    {
      dataField: 'status',
      text: t('Communication Status'),
      formatter: statusFormatter,
      classes: 'border-0 align-middle fs-0',
      headerClasses: 'border-0',
      sort: true,
      align: 'right',
      headerAlign: 'right'
    },
    {
      dataField: 'action',
      classes: 'border-0 align-middle',
      headerClasses: 'border-0',
      text: ''
    }
  ];

  return (
    <PaginationProvider pagination={paginationFactory(options)}>
      {({ paginationProps, paginationTableProps }) => {
        const lastIndex = paginationProps.page * paginationProps.sizePerPage;

        return (
          <Fragment>
            <div className="table-responsive">
              <BootstrapTable
                ref={table}
                bootstrap4
                keyField="id"
                data={microgridList}
                columns={columns}
                selectRow={selectRow(onSelect)}
                bordered={false}
                classes="table-dashboard table-sm fs--1 border-bottom border-200 mb-0 table-dashboard-th-nowrap"
                rowClasses="btn-reveal-trigger border-top border-200"
                headerClasses="bg-200 text-900 border-y border-200"
                {...paginationTableProps}
              />
            </div>
            <Row noGutters className="px-1 py-3">
              <Col className="pl-3 fs--1">
                <CustomTotal {...paginationProps} lastIndex={lastIndex} />
                <ButtonIcon
                  color="link"
                  size="sm"
                  icon="chevron-right"
                  iconAlign="right"
                  transform="down-1 shrink-4"
                  className="px-0 font-weight-semi-bold"
                  onClick={() => handleViewAll(paginationProps, microgridList.length)}
                >
                  {t('View all')}
                </ButtonIcon>
              </Col>
              <Col xs="auto" className="pr-3">
                <Button
                  color={paginationProps.page === 1 ? 'light' : 'primary'}
                  size="sm"
                  onClick={handlePrevPage(paginationProps)}
                  disabled={paginationProps.page === 1}
                  className="px-4"
                >
                  {t('Previous Page')}
                </Button>
                <Button
                  color={lastIndex >= paginationProps.totalSize ? 'light' : 'primary'}
                  size="sm"
                  onClick={handleNextPage(paginationProps)}
                  disabled={lastIndex >= paginationProps.totalSize}
                  className="px-4 ml-2"
                >
                  {t('Next Page')}
                </Button>
              </Col>
            </Row>
          </Fragment>
        );
      }}
    </PaginationProvider>
  );
};

export default withTranslation()(MicrogridTable);
